// src/components/CalendarOverview.js
import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { API_URL } from "./apiurl";

import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { registerLocale } from "react-datepicker";
import de from "date-fns/locale/de";
import { format, addMonths, subMonths } from "date-fns";

// React-Select für Kunden
import Select from "react-select";
import makeAnimated from "react-select/animated";

registerLocale("de", de);

/** Rundet float auf 0,25-Schritte */
function roundToQuarter(value) {
  const num = parseFloat(value);
  if (isNaN(num)) return 0;
  return Math.round(num * 4) / 4;
}

/** Ermittelt ISO-8601-KW (vereinfacht) */
function getWeekNumber(dateObj) {
  const d = new Date(
    Date.UTC(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate())
  );
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(d.getFullYear(), 0, 1));
  return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
}

/** Zählt Werktage im Monat (Mo–Fr) */
function getWorkdaysInMonth(year, month) {
  let count = 0;
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  for (let d = new Date(first); d <= last; d.setDate(d.getDate() + 1)) {
    const dow = d.getDay();
    if (dow >= 1 && dow <= 5) {
      count++;
    }
  }
  return count;
}

/** Baut das Monats-Grid (Wochen/Zeilen, 7 Spalten). Tage außerhalb des Monats => null */
function createCalendarMatrix(year, month) {
  const matrix = [];
  const firstDayOfMonth = new Date(year, month, 1);
  const lastDayOfMonth = new Date(year, month + 1, 0);

  let dayOfWeek = firstDayOfMonth.getDay();
  if (dayOfWeek === 0) dayOfWeek = 7;
  const startDate = new Date(firstDayOfMonth);
  startDate.setDate(startDate.getDate() - (dayOfWeek - 1));

  let current = new Date(startDate);

  while (true) {
    const weekRow = [];
    for (let i = 0; i < 7; i++) {
      if (current > new Date(lastDayOfMonth.getTime() + 7 * 86400000)) {
        weekRow.push(null);
      } else {
        if (current >= firstDayOfMonth && current <= lastDayOfMonth) {
          weekRow.push(new Date(current));
        } else {
          weekRow.push(null);
        }
      }
      current.setDate(current.getDate() + 1);
    }
    matrix.push(weekRow);

    if (current > new Date(lastDayOfMonth.getTime() + 7 * 86400000)) {
      break;
    }
    if (matrix.length > 6) break;
  }
  return matrix;
}

/** TT.MM.JJJJ */
function formatDateToGerman(dateObj) {
  if (!dateObj) return "";
  const dd = String(dateObj.getDate()).padStart(2, "0");
  const mm = String(dateObj.getMonth() + 1).padStart(2, "0");
  const yyyy = dateObj.getFullYear();
  return `${dd}.${mm}.${yyyy}`;
}

/** Parse YYYY-MM-DD => Date */
function parseDate(str) {
  if (!str) return null;
  const [yyyy, mm, dd] = str.split("-");
  if (!dd || !mm || !yyyy) return null;
  return new Date(+yyyy, +mm - 1, +dd);
}

/**
 * Custom input for DatePicker to allow custom styling
 */
const CustomDateInput = React.forwardRef(
  ({ value, onClick, placeholder }, ref) => (
    <div className="date-picker-wrapper" onClick={onClick}>
      <input
        type="text"
        value={value}
        placeholder={placeholder}
        ref={ref}
        className="date-picker-input"
        readOnly
      />
      <span className="date-picker-icon">📅</span>
    </div>
  )
);

// Komponente für einen einzelnen Kalendertag
const CalendarDay = ({
  dayObj,
  dailyTarget,
  entries,
  selectedDate,
  onDayClick,
  colors,
}) => {
  if (!dayObj) return <div className="calendar-day empty"></div>;

  const formattedDS = format(dayObj, "yyyy-MM-dd");
  const isToday = new Date().toDateString() === dayObj.toDateString();
  const isSelected = selectedDate === formattedDS;
  const dayEntries = entries.filter((e) => e.date === formattedDS);
  const entryTypes = dayEntries.map((e) => e.type);
  const istSum = dayEntries.reduce((acc, e) => acc + (e.hours || 0), 0);

  // Bestimme Hintergrundfarbe
  let bgColor = "";
  if (entryTypes.includes("feiertag")) bgColor = colors.feiertag;
  else if (entryTypes.includes("urlaub")) bgColor = colors.urlaub;
  else if (entryTypes.includes("krank")) bgColor = colors.krank;
  else if (istSum <= 0) bgColor = colors.neutral;
  else if (Math.abs(istSum - dailyTarget) < 0.001) bgColor = colors.exact;
  else if (istSum > dailyTarget) bgColor = colors.over;
  else bgColor = colors.under; // 0 < ist < soll

  const dayClasses = [
    "calendar-day",
    isToday ? "today" : "",
    isSelected ? "selected" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const diffHours = istSum - dailyTarget;

  return (
    <div
      className={dayClasses}
      onClick={() => onDayClick(dayObj)}
      style={{ backgroundColor: bgColor }}
    >
      <div className="day-number">{dayObj.getDate()}</div>
      {istSum > 0 && (
        <div className="day-content">
          <div className="day-hours">{istSum.toFixed(2)} Std</div>
          {diffHours > 0 && (
            <div className="day-diff">+{diffHours.toFixed(2)}</div>
          )}
        </div>
      )}
      {dayEntries.length > 0 && (
        <div className="entry-indicator">{dayEntries.length} Eintr.</div>
      )}
    </div>
  );
};

// Haupt-Komponente für die Kalenderansicht
export default function CalendarOverview() {
  const { auth, logout } = useContext(AuthContext);
  const navigate = useNavigate();

  // State für Kunden
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Allgemeiner State
  const [date, setDate] = useState(new Date());
  const [entries, setEntries] = useState([]);
  const [monthlyHours, setMonthlyHours] = useState(160);
  const [colors, setColors] = useState({
    over: "#10b981", // Grün für Überstunden (positiv)
    exact: "#3b82f6", // Blau für genau erreicht
    under: "#ef4444", // Rot für Unterstunden
    urlaub: "#eab308", // Gelb für Urlaub
    feiertag: "#f97316", // Orange für Feiertag
    krank: "#9ca3af", // Grau für Krankheit
    neutral: "#f3f4f6", // Hellgrau für neutralen Hintergrund
  });

  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [dailyTarget, setDailyTarget] = useState(8);
  const [selectedDate, setSelectedDate] = useState(null);

  const [stundensatzJahr, setStundensatzJahr] = useState(0);
  const [allesStundenSaetzeJahre, setAllesStundenSaetzeJahre] = useState({});
  const [stundensatzFachgebiet, setStundensatzFachgebiet] = useState(0);

  const [editingEntry, setEditingEntry] = useState(null);
  const [showNewEntryForm, setShowNewEntryForm] = useState(false);
  const [formData, setFormData] = useState({
    date: format(new Date(), "yyyy-MM-dd"),
    type: "normal",
    subject: "",
    address: "",
    startTime: "",
    endTime: "",
    hours: "",
    hotelspeesen: false,
    verpflegungsMehraufwand: false,
    hourlyWage: "",
    customerId: null,
    fachgebiet: 0,
    homeoffice: false,
    abgerechnet: false,
    aenderungsGrund: "",
    aenderungsText: "",
  });

  const weekdays = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
  const monthNames = [
    "Januar",
    "Februar",
    "März",
    "April",
    "Mai",
    "Juni",
    "Juli",
    "August",
    "September",
    "Oktober",
    "November",
    "Dezember",
  ];
  const [activeTab, setActiveTab] = useState("general");
  // Kunden laden
  const fetchCustomers = async () => {
    try {
      const response = await fetch(`${API_URL}/customers`, {
        headers: {
          Authorization: `Bearer ${auth.token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Fehler beim Laden der Kunden.");
      }

      const data = await response.json();
      setCustomers(data);
    } catch (err) {
      console.error("Fehler beim Laden der Kunden:", err);
      toast.error(err.message || "Fehler beim Laden der Kunden.");
    }
  };

  // Kunden-Map erstellen für schnellen Zugriff
  const customerMap = {};
  customers.forEach((cust) => {
    customerMap[cust.id] = cust;
  });

  // Einstellungen und Einträge laden
  useEffect(() => {
    if (!auth.token) {
      navigate("/login");
      return;
    }

    // Einstellungen laden
    fetch(`${API_URL}/settings`, {
      headers: { Authorization: `Bearer ${auth.token}` },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data) {
          setMonthlyHours(data.monthlyHours ?? 160);
          if (data.colors) setColors(data.colors);

          const stundensatzJahre =
            typeof data.hourlyWage === "string"
              ? JSON.parse(data.hourlyWage)
              : data.hourlyWage;
          setAllesStundenSaetzeJahre(stundensatzJahre);

          if (stundensatzJahre[currentYear]) {
            setStundensatzJahr(stundensatzJahre[currentYear].stundensatz);
            setStundensatzFachgebiet(stundensatzJahre[currentYear].fachgebiet);
          } else {
            setStundensatzJahr(0);
          }
        }
      })
      .catch((error) => {
        console.error("Fehler beim Laden der Einstellungen:", error);
        toast.error("Einstellungen konnten nicht geladen werden.");
      });

    // Kunden laden
    fetchCustomers();

    // Einträge laden
    fetch(`${API_URL}/entries`, {
      headers: { Authorization: `Bearer ${auth.token}` },
    })
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setEntries(data);
        } else {
          setEntries([]);
          if (data.error === "Failed to authenticate token") {
            logout();
            navigate("/login");
          }
        }
      })
      .catch((error) => {
        console.error("Fehler beim Laden der Einträge:", error);
        toast.error("Einträge konnten nicht geladen werden.");
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.token, navigate, logout, currentYear]);

  // Tägliches Soll berechnen
  useEffect(() => {
    const wd = getWorkdaysInMonth(currentYear, currentMonth);
    if (wd > 0) {
      const raw = monthlyHours / wd;
      setDailyTarget(roundToQuarter(raw));
    } else {
      setDailyTarget(0);
    }
  }, [monthlyHours, currentYear, currentMonth]);

  // Kalendermatrix erzeugen
  const matrix = createCalendarMatrix(currentYear, currentMonth);

  // Monats-Filter für Einträge
  const monthlyFiltered = entries.filter((e) => {
    const d = parseDate(e.date);
    if (!d) return false;
    return d.getFullYear() === currentYear && d.getMonth() === currentMonth;
  });

  // Einträge für den ausgewählten Tag oder den ganzen Monat
  const finalFiltered = selectedDate
    ? monthlyFiltered.filter((e) => e.date === selectedDate)
    : monthlyFiltered;

  // Summen berechnen
  const totalIstHours = monthlyFiltered.reduce(
    (acc, e) => acc + (e.hours || 0),
    0
  );
  const totalIstEur = monthlyFiltered.reduce(
    (acc, e) => acc + (e.hours || 0) * (e.hourlyWage || 0),
    0
  );
  const totalSollEur = (monthlyHours * stundensatzJahr).toFixed(2);
  const diffHours = totalIstHours - monthlyHours;
  const diffPercent =
    monthlyHours > 0 ? ((totalIstHours / monthlyHours) * 100).toFixed(1) : 0;

  // Vormonat anzeigen
  const prevMonth = () => {
    const prevDate = subMonths(new Date(currentYear, currentMonth), 1);
    setCurrentMonth(prevDate.getMonth());
    setCurrentYear(prevDate.getFullYear());
    setSelectedDate(null);
    updateYearlyWage(prevDate.getFullYear());
  };

  // Nächsten Monat anzeigen
  const nextMonth = () => {
    const nextDate = addMonths(new Date(currentYear, currentMonth), 1);
    setCurrentMonth(nextDate.getMonth());
    setCurrentYear(nextDate.getFullYear());
    setSelectedDate(null);
    updateYearlyWage(nextDate.getFullYear());
  };

  // Zum aktuellen Monat springen
  const goToCurrentMonth = () => {
    const today = new Date();
    setCurrentMonth(today.getMonth());
    setCurrentYear(today.getFullYear());
    setSelectedDate(null);
    updateYearlyWage(today.getFullYear());
  };

  // Stundensatz für das Jahr aktualisieren
  const updateYearlyWage = (year) => {
    if (allesStundenSaetzeJahre[year]) {
      setStundensatzJahr(allesStundenSaetzeJahre[year].stundensatz);
      setStundensatzFachgebiet(allesStundenSaetzeJahre[year].fachgebiet || 0);
    } else {
      setStundensatzJahr(0);
      setStundensatzFachgebiet(0);
    }
  };

  // Klick auf Tag im Kalender
  function handleDayClick(dayObj) {
    if (!dayObj) return;
    const ds = format(dayObj, "yyyy-MM-dd");
    setSelectedDate((prevDate) => (prevDate === ds ? null : ds));
  }

  // Eintrag bearbeiten
  const handleEdit = (entry) => {
    setEditingEntry(entry);
    setShowNewEntryForm(false);

    // Formular mit Eintragsdaten füllen
    setFormData({
      date: entry.date,
      type: entry.type || "normal",
      subject: entry.subject || "",
      address: entry.address || "",
      startTime: entry.startTime || "",
      endTime: entry.endTime || "",
      hours: entry.hours || "",
      hotelspeesen: entry.hotelspeesen || false,
      verpflegungsMehraufwand: entry.verpflegungsMehraufwand || false,
      hourlyWage: entry.hourlyWage || stundensatzJahr,
      customerId: entry.customerId || null,
      fachgebiet: entry.fachgebiet || 0,
      homeoffice: entry.homeoffice || false,
      abgerechnet: entry.abgerechnet ? true : false,
      aenderungsGrund: entry.aenderungsGrund || "",
      aenderungsText: entry.aenderungsText || "",
    });

    // DatePicker
    setDate(parseDate(entry.date));

    // react-select für Kunde
    const foundCustomer = customerOptions.find(
      (opt) => opt.value === entry.customerId
    );
    setSelectedCustomer(foundCustomer || null);
  };

  // Neuen Eintrag erstellen
  const handleNewEntry = () => {
    setEditingEntry(null);
    setShowNewEntryForm(true);

    // Formular zurücksetzen
    const defaultDate = selectedDate || format(new Date(), "yyyy-MM-dd");
    setFormData({
      date: defaultDate,
      type: "normal",
      subject: "",
      address: "",
      startTime: "",
      endTime: "",
      hours: "",
      hotelspeesen: false,
      verpflegungsMehraufwand: false,
      hourlyWage: stundensatzJahr,
      customerId: null,
      fachgebiet: 0,
      homeoffice: false,
      abgerechnet: false,
      aenderungsGrund: "",
      aenderungsText: "",
    });

    setDate(parseDate(defaultDate));
    setSelectedCustomer(null);
  };

  // Bearbeiten abbrechen
  const handleCancelEdit = () => {
    setEditingEntry(null);
    setShowNewEntryForm(false);
  };

  // Änderungen im Formular erfassen
  const handleFormChange = (e) => {
    const { name, type, checked, value } = e.target;
  
    if (type === "checkbox") {
      if (name === "Workflow") {
        setFormData((prev) => ({
          ...prev,
          fachgebiet: checked ? 1 : 0,
          hourlyWage: checked ? stundensatzFachgebiet : stundensatzJahr,
        }));
      } else if (name === "Finanzbuchhaltung") {
        setFormData((prev) => ({
          ...prev,
          fachgebiet: checked ? 2 : 0,
          hourlyWage: checked ? stundensatzFachgebiet : stundensatzJahr,
        }));
      } else if (name === "abgerechnet") {
        setFormData((prev) => ({
          ...prev,
          abgerechnet: checked,
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          [name]: checked,
        }));
      }
    } else {
      setFormData((prev) => {
        const updatedData = {
          ...prev,
          [name]: value,
        };
  
        // Automatische Berechnung der Stunden, wenn Start- oder Endzeit geändert wurde
        if (name === "startTime" || name === "endTime") {
          if (updatedData.startTime && updatedData.endTime) {
            // Startzeit in Minuten umrechnen
            const [startHours, startMinutes] = updatedData.startTime.split(":").map(Number);
            const startTimeInMinutes = startHours * 60 + startMinutes;
            
            // Endzeit in Minuten umrechnen
            const [endHours, endMinutes] = updatedData.endTime.split(":").map(Number);
            const endTimeInMinutes = endHours * 60 + endMinutes;
            
            // Falls Endzeit vor Startzeit liegt, wird angenommen, dass die Endzeit am nächsten Tag ist
            let differenceInMinutes = endTimeInMinutes - startTimeInMinutes;
            if (differenceInMinutes < 0) {
              differenceInMinutes += 24 * 60; // 24 Stunden in Minuten
            }
            
            // Umrechnung in Stunden mit 2 Dezimalstellen
            const hours = Math.round((differenceInMinutes / 60) * 4) / 4; // Runden auf 0.25 Schritte
            updatedData.hours = hours;
          }
        }
        
        return updatedData;
      });
    }
  };

  // DatePicker-Änderung
  const handleDateChange = (selectedDateObj) => {
    setDate(selectedDateObj);
    if (selectedDateObj) {
      const formattedDate = format(selectedDateObj, "yyyy-MM-dd");
      setFormData((prev) => ({
        ...prev,
        date: formattedDate,
      }));
    }
  };

  // Kunde ändern (react-select)
  const animatedComponents = makeAnimated();

  const handleCustomerChange = (option) => {
    setSelectedCustomer(option);
    setFormData((prev) => ({
      ...prev,
      customerId: option ? option.value : null,
    }));
  };

  // Eintrag aktualisieren
  const handleUpdate = async (e) => {
    e.preventDefault();

    const updatedEntry = {
      ...formData,
      hours: parseFloat(formData.hours) || 0,
    };

    try {
      const response = await fetch(`${API_URL}/entries/${editingEntry.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${auth.token}`,
        },
        body: JSON.stringify(updatedEntry),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error || "Fehler beim Aktualisieren des Eintrags."
        );
      }

      const data = await response.json();
      setEntries((prev) =>
        prev.map((entry) => (entry.id === data.id ? data : entry))
      );
      toast.success("Eintrag erfolgreich aktualisiert.");
      handleCancelEdit();
    } catch (err) {
      toast.error(err.message || "Serverfehler.");
    }
  };

  // Neuen Eintrag erstellen
  const handleCreate = async (e) => {
    e.preventDefault();

    const newEntry = {
      ...formData,
      hours: parseFloat(formData.hours) || 0,
    };

    try {
      const response = await fetch(`${API_URL}/entries`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${auth.token}`,
        },
        body: JSON.stringify(newEntry),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error || "Fehler beim Erstellen des Eintrags."
        );
      }

      const data = await response.json();
      setEntries((prev) => [...prev, data]);
      toast.success("Eintrag erfolgreich erstellt.");
      handleCancelEdit();
    } catch (err) {
      toast.error(err.message || "Serverfehler.");
    }
  };

  // Eintrag löschen
  const handleDelete = async (id) => {
    if (!window.confirm("Wirklich löschen?")) return;
    try {
      const resp = await fetch(`${API_URL}/entries/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${auth.token}` },
      });
      if (!resp.ok) {
        const errData = await resp.json();
        toast.error(errData.error || "Lösch-Fehler.");
        return;
      }
      setEntries((prev) => prev.filter((e) => e.id !== id));
      toast.success("Eintrag erfolgreich gelöscht.");
    } catch (err) {
      toast.error("Fehler beim Löschen des Eintrags.");
    }
  };

  // Kundenoptionen für react-select
  const customerOptions = customers.map((c) => ({
    value: c.id,
    label: `${c.vorname || ""} ${c.nachname || ""}`.trim(),
  }));

  // Gruppen von Eintragstypen erstellen
  const entryTypeGroups = [
    {
      label: "Standard",
      options: [
        { value: "normal", label: "Normal" },
        { value: "halb-vormittag", label: "Halber Tag (Vormittag)" },
        { value: "halb-nachmittag", label: "Halber Tag (Nachmittag)" },
      ],
    },
    {
      label: "Abwesenheit",
      options: [
        { value: "urlaub", label: "Urlaub" },
        { value: "feiertag", label: "Feiertag" },
        { value: "krank", label: "Krank" },
      ],
    },
  ];

  return (
    <div className="calendar-overview">
      <div className="dashboard-header">
        <h1>Zeiterfassung</h1>

        <div className="month-navigation">
          <button className="icon-button" onClick={prevMonth}>
            ◀
          </button>

          <h2 className="current-month">
            {monthNames[currentMonth]} {currentYear}
          </h2>

          <button className="icon-button" onClick={nextMonth}>
            ▶
          </button>

          <button className="today-button" onClick={goToCurrentMonth}>
            Heute
          </button>

          <button className="new-entry-button" onClick={handleNewEntry}>
            + Neuer Eintrag
          </button>
        </div>
      </div>

      <div className="dashboard-content">
        <div className="summary-cards">
          <div className="summary-card">
            <div className="card-title">Monatssoll</div>
            <div className="card-value">{monthlyHours} Std</div>
            <div className="card-secondary">{totalSollEur} €</div>
          </div>

          <div className="summary-card">
            <div className="card-title">Tages-Soll</div>
            <div className="card-value">{dailyTarget.toFixed(2)} Std</div>
            <div className="card-secondary">pro Arbeitstag</div>
          </div>

          <div className="summary-card">
            <div className="card-title">IST (Monat)</div>
            <div className="card-value">{totalIstHours.toFixed(2)} Std</div>
            <div className="card-secondary">{totalIstEur.toFixed(2)} €</div>
          </div>

          <div className="summary-card">
            <div className="card-title">Differenz</div>
            <div
              className={`card-value ${
                diffHours >= 0 ? "positive" : "negative"
              }`}
            >
              {diffHours >= 0 ? "+" : ""}
              {diffHours.toFixed(2)} Std
            </div>
            <div className="card-secondary">{diffPercent}% vom Soll</div>
          </div>
        </div>

        <div className="calendar-container">
          <div className="weekday-header">
            {weekdays.map((day) => (
              <div key={day} className="weekday">
                {day}
              </div>
            ))}
          </div>

          <div className="calendar-grid">
            {matrix.map((weekRow, weekIndex) => (
              <React.Fragment key={`week-${weekIndex}`}>
                {weekRow.map((dayObj, dayIndex) => (
                  <CalendarDay
                    key={`day-${weekIndex}-${dayIndex}`}
                    dayObj={dayObj}
                    dailyTarget={dailyTarget}
                    entries={entries}
                    selectedDate={selectedDate}
                    onDayClick={handleDayClick}
                    colors={colors}
                  />
                ))}
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="entries-container">
          <div className="entries-header">
            <h3>
              {selectedDate
                ? `Einträge für ${formatDateToGerman(parseDate(selectedDate))}`
                : "Alle Einträge im Monat"}
            </h3>

            {selectedDate && (
              <button
                className="clear-filter-button"
                onClick={() => setSelectedDate(null)}
              >
                Alle anzeigen
              </button>
            )}
          </div>

          {finalFiltered.length === 0 ? (
            <div className="no-entries">
              <p>
                Keine Einträge{" "}
                {selectedDate ? "für diesen Tag" : "in diesem Monat"}
              </p>
              <button className="add-entry-button" onClick={handleNewEntry}>
                + Eintrag hinzufügen
              </button>
            </div>
          ) : (
            <div className="entries-list">
              {finalFiltered.map((entry) => (
                <div key={entry.id} className="entry-card">
                  <div className="entry-header">
                    <div className="entry-date">
                      {formatDateToGerman(parseDate(entry.date))}
                    </div>
                    <div className="entry-type">{entry.type}</div>
                    <div className="entry-hours">
                      {(entry.hours || 0).toFixed(2)} Std
                    </div>
                  </div>

                  <div className="entry-content">
                    <div className="entry-subject">{entry.subject}</div>
                    <div className="entry-customer">
                      {customerMap[entry.customerId]?.nachname || "Kein Kunde"}
                    </div>
                    {entry.address && (
                      <div className="entry-address">{entry.address}</div>
                    )}
                  </div>

                  <div className="entry-footer">
                    <div className="entry-tags">
                      {Boolean(entry.homeoffice) && (
                        <span className="tag">Homeoffice</span>
                      )}
                      {Boolean(entry.hotelspeesen) && (
                        <span className="tag">Hotel</span>
                      )}
                      {Boolean(entry.verpflegungsMehraufwand) && (
                        <span className="tag">Verpflegung</span>
                      )}
                      {entry.fachgebiet === 1 && (
                        <span className="tag">Workflow</span>
                      )}
                      {entry.fachgebiet === 2 && (
                        <span className="tag">Finanzbuchhaltung</span>
                      )}
                      {Boolean(entry.abgerechnet) && (
                        <span className="tag success">Abgerechnet</span>
                      )}
                    </div>

                    <div className="entry-actions">
                      <button
                        className="action-button edit"
                        onClick={() => handleEdit(entry)}
                      >
                        Bearbeiten
                      </button>
                      <button
                        className="action-button delete"
                        onClick={() => handleDelete(entry.id)}
                      >
                        Löschen
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Modal für Bearbeiten und Neu */}
      {(editingEntry || showNewEntryForm) && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{editingEntry ? "Eintrag bearbeiten" : "Neuer Eintrag"}</h3>
              <button className="close-button" onClick={handleCancelEdit}>
                ×
              </button>
            </div>

            <form
              onSubmit={editingEntry ? handleUpdate : handleCreate}
              className="entry-form"
            >
              <div className="form-tabs">
                <div
                  className={`tab ${activeTab === "general" ? "active" : ""}`}
                  onClick={() => setActiveTab("general")}
                >
                  Allgemein
                </div>
                <div
                  className={`tab ${activeTab === "extended" ? "active" : ""}`}
                  onClick={() => setActiveTab("extended")}
                >
                  Erweitert
                </div>
              </div>

              <div className="form-sections">
                <div
                  className={`form-section ${
                    activeTab !== "general" ? "hidden" : ""
                  }`}
                >
                  <div className="form-row">
                    <div className="form-group">
                      <label>Datum</label>
                      <DatePicker
                        selected={parseDate(formData.date)}
                        onChange={handleDateChange}
                        dateFormat="dd.MM.yyyy"
                        locale="de"
                        placeholderText="TT.MM.JJJJ"
                        maxDate={new Date(2100, 11, 31)}
                        showMonthDropdown
                        showYearDropdown
                        dropdownMode="select"
                        customInput={<CustomDateInput />}
                      />
                    </div>

                    <div className="form-group">
                      <label>Art des Tages</label>
                      <Select
                        value={entryTypeGroups
                          .flatMap((group) => group.options)
                          .find((option) => option.value === formData.type)}
                        onChange={(option) =>
                          handleFormChange({
                            target: { name: "type", value: option.value },
                          })
                        }
                        options={entryTypeGroups}
                        className="react-select"
                        placeholder="Art auswählen"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Startzeit</label>
                      <input
                        type="time"
                        name="startTime"
                        value={formData.startTime}
                        onChange={handleFormChange}
                        className="form-input"
                      />
                    </div>

                    <div className="form-group">
                      <label>Endzeit</label>
                      <input
                        type="time"
                        name="endTime"
                        value={formData.endTime}
                        onChange={handleFormChange}
                        className="form-input"
                      />
                    </div>

                    <div className="form-group">
                      <label>Stunden</label>
                      <input
                        type="number"
                        name="hours"
                        step="0.25"
                        value={formData.hours}
                        onChange={handleFormChange}
                        className="form-input"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Betreff / Auftrag</label>
                    <textarea
                      name="subject"
                      value={formData.subject}
                      onChange={handleFormChange}
                      className="form-textarea"
                      rows="2"
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Kunde</label>
                      <Select
                        value={selectedCustomer}
                        onChange={handleCustomerChange}
                        options={customerOptions}
                        isClearable
                        placeholder="Kunde auswählen"
                        className="react-select"
                        components={animatedComponents}
                      />
                    </div>

                    <div className="form-group">
                      <label>Adresse</label>
                      <input
                        type="text"
                        name="address"
                        value={formData.address}
                        onChange={handleFormChange}
                        className="form-input"
                      />
                    </div>
                  </div>

                  <div className="form-row checkbox-group">
                    <div className="form-check">
                      <input
                        type="checkbox"
                        id="homeoffice"
                        name="homeoffice"
                        checked={formData.homeoffice}
                        onChange={handleFormChange}
                      />
                      <label htmlFor="homeoffice">Homeoffice</label>
                    </div>

                    <div className="form-check">
                      <input
                        type="checkbox"
                        id="Workflow"
                        name="Workflow"
                        checked={formData.fachgebiet === 1}
                        onChange={handleFormChange}
                      />
                      <label htmlFor="Workflow">Workflow</label>
                    </div>

                    <div className="form-check">
                      <input
                        type="checkbox"
                        id="Finanzbuchhaltung"
                        name="Finanzbuchhaltung"
                        checked={formData.fachgebiet === 2}
                        onChange={handleFormChange}
                      />
                      <label htmlFor="Finanzbuchhaltung">
                        Finanzbuchhaltung
                      </label>
                    </div>

                    <div className="form-check">
                      <input
                        type="checkbox"
                        id="abgerechnet"
                        name="abgerechnet"
                        checked={formData.abgerechnet}
                        onChange={handleFormChange}
                      />
                      <label htmlFor="abgerechnet">Abgerechnet</label>
                    </div>
                  </div>
                </div>

                {/* Erweiterte Optionen (inaktiv in dieser Ansicht) */}
                <div
                  className={`form-section ${
                    activeTab !== "extended" ? "hidden" : ""
                  }`}
                >
                  <div className="form-row">
                    <div className="form-group">
                      <label>Stundensatz</label>
                      <input
                        type="number"
                        name="hourlyWage"
                        step="0.01"
                        value={formData.hourlyWage}
                        onChange={handleFormChange}
                        className="form-input"
                      />
                    </div>
                  </div>

                  <div className="form-row checkbox-group">
                    <div className="form-check">
                      <input
                        type="checkbox"
                        id="hotelspeesen"
                        name="hotelspeesen"
                        checked={formData.hotelspeesen}
                        onChange={handleFormChange}
                      />
                      <label htmlFor="hotelspeesen">Hotelspeesen</label>
                    </div>

                    <div className="form-check">
                      <input
                        type="checkbox"
                        id="verpflegungsMehraufwand"
                        name="verpflegungsMehraufwand"
                        checked={formData.verpflegungsMehraufwand}
                        onChange={handleFormChange}
                      />
                      <label htmlFor="verpflegungsMehraufwand">
                        Verpflegungsmehraufwand
                      </label>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Änderungsgrund</label>
                    <input
                      type="text"
                      name="aenderungsGrund"
                      value={formData.aenderungsGrund}
                      onChange={handleFormChange}
                      className="form-input"
                    />
                  </div>

                  <div className="form-group">
                    <label>Änderungstext</label>
                    <textarea
                      name="aenderungsText"
                      value={formData.aenderungsText}
                      onChange={handleFormChange}
                      className="form-textarea"
                      rows="2"
                    />
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="cancel-button"
                  onClick={handleCancelEdit}
                >
                  Abbrechen
                </button>
                <button type="submit" className="submit-button">
                  {editingEntry ? "Aktualisieren" : "Erstellen"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* CSS für die Komponente */}
      <style jsx>{`
        .calendar-overview {
          font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
          max-width: 1200px;
          margin: 0 auto;
          padding: 20px;
          color: #333;
        }

        .dashboard-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }

        .dashboard-header h1 {
          font-size: 24px;
          font-weight: 600;
          margin: 0;
        }

        .month-navigation {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .current-month {
          font-size: 18px;
          font-weight: 500;
          margin: 0;
          min-width: 150px;
          text-align: center;
        }

        .icon-button {
          background: none;
          border: 1px solid #e0e0e0;
          border-radius: 4px;
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          font-size: 14px;
          color: #555;
        }

        .icon-button:hover {
          background-color: #f5f5f5;
        }

        .today-button {
          background-color: #fff;
          border: 1px solid #e0e0e0;
          border-radius: 4px;
          padding: 8px 12px;
          cursor: pointer;
          font-size: 14px;
        }

        .today-button:hover {
          background-color: #f5f5f5;
        }

        .new-entry-button {
          background-color: #4285f4;
          color: white;
          border: none;
          border-radius: 4px;
          padding: 8px 16px;
          cursor: pointer;
          font-size: 14px;
          margin-left: 20px;
        }

        .new-entry-button:hover {
          background-color: #3367d6;
        }

        .summary-cards {
          display: flex;
          gap: 20px;
          margin-bottom: 20px;
        }

        .summary-card {
          background-color: white;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
          padding: 16px;
          flex: 1;
          min-width: 0;
          border-top: 3px solid #4285f4;
        }

        .card-title {
          font-size: 14px;
          color: #666;
          margin-bottom: 4px;
        }

        .card-value {
          font-size: 24px;
          font-weight: 600;
          color: #333;
          margin-bottom: 4px;
        }

        .card-value.positive {
          color: #0f9d58;
        }

        .card-value.negative {
          color: #db4437;
        }

        .card-secondary {
          font-size: 13px;
          color: #666;
        }

        .calendar-container {
          background-color: white;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
          padding: 16px;
          margin-bottom: 20px;
        }

        .weekday-header {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          gap: 8px;
          margin-bottom: 8px;
        }

        .weekday {
          text-align: center;
          font-weight: 500;
          padding: 8px;
          color: #666;
        }

        .calendar-grid {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          gap: 8px;
        }

        .calendar-day {
          border: 1px solid #e0e0e0;
          border-radius: 4px;
          min-height: 100px;
          padding: 8px;
          cursor: pointer;
          position: relative;
          transition: all 0.2s ease;
        }

        .calendar-day:hover {
          border-color: #bbb;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
        }

        .calendar-day.today {
          border: 2px solid #4285f4;
        }

        .calendar-day.selected {
          box-shadow: 0 0 0 2px #4285f4;
        }

        .calendar-day.empty {
          background-color: #f9f9f9;
          border: 1px dashed #e0e0e0;
          cursor: default;
        }

        .day-number {
          font-weight: 600;
          color: #333;
          margin-bottom: 4px;
          font-size: 16px;
        }

        .day-content {
          margin-top: 8px;
        }

        .day-hours {
          font-size: 14px;
          font-weight: 500;
        }

        .day-diff {
          font-size: 12px;
          color: #0f9d58;
          font-weight: 500;
        }

        .entry-indicator {
          position: absolute;
          bottom: 8px;
          right: 8px;
          font-size: 11px;
          background-color: rgba(0, 0, 0, 0.1);
          padding: 2px 6px;
          border-radius: 10px;
        }

        .entries-container {
          background-color: white;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
          padding: 16px;
        }

        .entries-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
          padding-bottom: 8px;
          border-bottom: 1px solid #eee;
        }

        .entries-header h3 {
          font-size: 18px;
          font-weight: 500;
          margin: 0;
        }

        .clear-filter-button {
          background: none;
          border: 1px solid #e0e0e0;
          border-radius: 4px;
          padding: 6px 12px;
          cursor: pointer;
          font-size: 13px;
        }

        .no-entries {
          text-align: center;
          padding: 40px 0;
          color: #666;
        }

        .add-entry-button {
          background-color: #4285f4;
          color: white;
          border: none;
          border-radius: 4px;
          padding: 8px 16px;
          cursor: pointer;
          font-size: 14px;
          margin-top: 16px;
        }

        .entries-list {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .entry-card {
          border: 1px solid #e0e0e0;
          border-radius: 8px;
          padding: 16px;
          transition: all 0.2s ease;
        }

        .entry-card:hover {
          border-color: #bbb;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
        }

        .entry-header {
          display: flex;
          justify-content: space-between;
          margin-bottom: 12px;
        }

        .entry-date {
          font-weight: 600;
          font-size: 14px;
        }

        .entry-type {
          font-size: 13px;
          background-color: #f5f5f5;
          padding: 2px 8px;
          border-radius: 4px;
        }

        .entry-hours {
          font-weight: 600;
          font-size: 16px;
        }

        .entry-content {
          margin-bottom: 16px;
        }

        .entry-subject {
          font-size: 15px;
          margin-bottom: 4px;
        }

        .entry-customer {
          font-size: 14px;
          color: #555;
          margin-bottom: 4px;
        }

        .entry-address {
          font-size: 13px;
          color: #777;
        }

        .entry-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-top: 12px;
          border-top: 1px solid #eee;
        }

        .entry-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
        }

        .tag {
          font-size: 12px;
          background-color: #f1f1f1;
          color: #666;
          padding: 2px 8px;
          border-radius: 12px;
        }

        .tag.success {
          background-color: #e6f4ea;
          color: #0f9d58;
        }

        .entry-actions {
          display: flex;
          gap: 8px;
        }

        .action-button {
          background: none;
          border: 1px solid #e0e0e0;
          border-radius: 4px;
          padding: 4px 10px;
          cursor: pointer;
          font-size: 13px;
        }

        .action-button.edit:hover {
          background-color: #f5f5f5;
        }

        .action-button.delete {
          color: #db4437;
          border-color: #db4437;
        }

        .action-button.delete:hover {
          background-color: #fce8e6;
        }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
        }

        .modal-content {
          background-color: white;
          border-radius: 8px;
          width: 600px;
          max-width: 90%;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
          padding: 15px;
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 20px;
          border-bottom: 1px solid #eee;
        }

        .modal-header h3 {
          font-size: 18px;
          font-weight: 500;
          margin: 0;
        }

        .close-button {
          background: none;
          border: none;
          font-size: 24px;
          cursor: pointer;
          color: #666;
        }

        .entry-form {
          padding: 20px;
        }

        .form-tabs {
          display: flex;
          border-bottom: 1px solid #eee;
          margin: -20px -20px 20px;
        }

        .tab {
          padding: 12px 20px;
          cursor: pointer;
          font-size: 14px;
          color: #666;
        }

        .tab.active {
          color: #4285f4;
          border-bottom: 2px solid #4285f4;
          font-weight: 500;
        }

        .form-sections {
          position: relative;
        }

        .form-section {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .form-section.hidden {
          display: none;
        }

        .form-row {
          display: flex;
          gap: 16px;
        }

        .form-group {
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .form-group label {
          font-size: 13px;
          color: #555;
        }

        .form-input,
        .form-textarea {
          border: 1px solid #ddd;
          border-radius: 4px;
          padding: 8px 12px;
          font-size: 14px;
          width: 100%;
        }

        .form-textarea {
          resize: vertical;
          min-height: 80px;
        }

        .date-picker-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }

        .date-picker-input {
          border: 1px solid #ddd;
          border-radius: 4px;
          padding: 8px 12px;
          font-size: 14px;
          width: 100%;
          cursor: pointer;
        }

        .date-picker-icon {
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          pointer-events: none;
        }

        .react-select {
          width: 100%;
        }

        .checkbox-group {
          display: flex;
          flex-wrap: wrap;
          gap: 20px;
        }

        .form-check {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .form-check input[type="checkbox"] {
          width: 16px;
          height: 16px;
        }

        .form-check label {
          font-size: 14px;
          cursor: pointer;
        }

        .form-actions {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          margin-top: 24px;
        }

        .cancel-button {
          background-color: white;
          border: 1px solid #ddd;
          border-radius: 4px;
          padding: 8px 16px;
          cursor: pointer;
          font-size: 14px;
        }

        .submit-button {
          background-color: #4285f4;
          color: white;
          border: none;
          border-radius: 4px;
          padding: 8px 16px;
          cursor: pointer;
          font-size: 14px;
        }

        .submit-button:hover {
          background-color: #3367d6;
        }

        /* Responsive Anpassungen */
        @media (max-width: 768px) {
          .summary-cards {
            flex-wrap: wrap;
          }

          .summary-card {
            flex-basis: calc(50% - 10px);
          }

          .form-row {
            flex-direction: column;
          }

          .month-navigation {
            flex-wrap: wrap;
          }
        }

        @media (max-width: 576px) {
          .summary-card {
            flex-basis: 100%;
          }

          .calendar-day {
            min-height: 80px;
          }

          .entry-header {
            flex-direction: column;
            gap: 8px;
          }

          .entry-footer {
            flex-direction: column;
            gap: 12px;
          }

          .entry-actions {
            width: 100%;
            justify-content: space-between;
          }
        }
      `}</style>
    </div>
  );
}
